import { AISchemaType } from "components/server-types";

/**
 * AIPseudoBlockType is our abstraction for worksheet blocks as represented
 * to the AI. Lots of the complexity of the worksheet schema is due to implementation
 * details, and AI tends to mess up the implementation details. They are also hard
 * to validate, since the schema is complex. This is our attempt to simplify the
 * schema for the AI, with a deterministic mapping from/to the worksheet schema.
 */
export type AIPseudoBlockType =
  | {
      block_type: "multiple_choice_single_answer" | "multiple_choice_multiple_answers";
      instructions: string;
      data: string[];
    }
  | {
      block_type: "table";
      instructions: string;
      data: string[][];
    }
  | {
      block_type: "free_response";
      instructions: string;
      data: null;
    }
  | {
      block_type: "file_upload";
      instructions: string;
      data: null;
    }
  | {
      block_type: "whiteboard";
      instructions: string;
      data: null;
    }
  | {
      /**
       * Instructor-only static text (Instructions block — no student response field).
       * Put the full prose in `data` (markdown). Named static_text so it is not confused with
       * the `instructions` field on question-style pseudo blocks.
       */
      block_type: "static_text";
      data: string;
    }
  | {
      /** Executable code cell (`data` = starter source as plain text, not markdown). */
      block_type: "code";
      instructions: string;
      data: string;
    };

const instructionsSchema = {
  type: "string",
  description: "Markdown string for instructions.",
};

/** One pseudo-block shape in JSON Schema (for append operations). */
const pseudoBlockSchemaOneOf = [
  {
    type: "object",
    additionalProperties: false,
    properties: {
      block_type: { type: "string", enum: ["static_text"] },
      data: {
        type: "string",
        description:
          "Full markdown for a static Instructions block (intro, context, reading — no answer field).",
      },
    },
    required: ["block_type", "data"],
  },
  {
    type: "object",
    additionalProperties: false,
    properties: {
      block_type: { type: "string", enum: ["multiple_choice_single_answer"] },
      instructions: instructionsSchema,
      data: {
        type: "array",
        items: { type: "string" },
        description: "Array of choice strings, plaintext, without any prefix (e.g. 'a)' or '1.')",
      },
    },
    required: ["block_type", "instructions", "data"],
  },
  {
    type: "object",
    additionalProperties: false,
    properties: {
      block_type: { type: "string", enum: ["multiple_choice_multiple_answers"] },
      instructions: instructionsSchema,
      data: {
        type: "array",
        items: { type: "string" },
        description: "Array of choice strings, plaintext, without any prefix (e.g. 'a)' or '1.')",
      },
    },
    required: ["block_type", "instructions", "data"],
  },
  {
    type: "object",
    additionalProperties: false,
    properties: {
      block_type: { type: "string", enum: ["free_response"] },
      instructions: instructionsSchema,
      data: {
        type: "null",
        description: "No data required for free response; students can fill in the blank.",
      },
    },
    required: ["block_type", "instructions", "data"],
  },
  {
    type: "object",
    additionalProperties: false,
    properties: {
      block_type: { type: "string", enum: ["table"] },
      instructions: instructionsSchema,
      data: {
        type: "array",
        items: { type: "array", items: { type: "string" } },
        description:
          "2D array of markdown strings where first row/column are headers (not modifiable by students). " +
          "Only the fields that are modifiable by students can be filled in.",
      },
    },
    required: ["block_type", "data"],
  },
  {
    type: "object",
    additionalProperties: false,
    properties: {
      block_type: { type: "string", enum: ["file_upload"] },
      instructions: instructionsSchema,
      data: {
        type: "null",
        description: "No data required for file upload; students can upload a file.",
      },
    },
    required: ["block_type", "instructions", "data"],
  },
  {
    type: "object",
    additionalProperties: false,
    properties: {
      block_type: { type: "string", enum: ["whiteboard"] },
      instructions: instructionsSchema,
      data: {
        type: "null",
        description: "No data required for whiteboard; students can draw on the whiteboard.",
      },
    },
    required: ["block_type", "instructions", "data"],
  },
  {
    type: "object",
    additionalProperties: false,
    properties: {
      block_type: { type: "string", enum: ["code"] },
      instructions: {
        ...instructionsSchema,
        description:
          "Optional markdown shown above the cell (prepended as a separate Instructions block when non-empty).",
      },
      data: {
        type: "string",
        description:
          "Initial code in the cell as plain text (language-agnostic; line breaks allowed).",
      },
    },
    required: ["block_type", "instructions", "data"],
  },
];

export type AIWorksheetOperation =
  | { type: "append"; blocks: AIPseudoBlockType[] }
  | { type: "delete_block"; block_id: string }
  | {
      type: "patch_field";
      block_id: string;
      field_id: string;
      text_markdown?: string;
      choice_labels?: string[];
      /** Plain text: replace CodeField starter source (`c`). Use with structure.code_field_id only. */
      code_source?: string;
    };

const operationSchemaOneOf = [
  {
    type: "object",
    additionalProperties: false,
    properties: {
      type: { type: "string", enum: ["append"] },
      blocks: {
        type: "array",
        description: "New blocks to add at the end of the worksheet (after deletes and patches).",
        minItems: 1,
        maxItems: 10,
        items: { oneOf: [...pseudoBlockSchemaOneOf] },
      },
    },
    required: ["type", "blocks"],
  },
  {
    type: "object",
    additionalProperties: false,
    properties: {
      type: { type: "string", enum: ["delete_block"] },
      block_id: {
        type: "string",
        description:
          "The worksheet block id (JSON key block_id on that block). Never use field ids (instructions_field_id, content_field_id, choice_field_id, code_field_id) here.",
      },
    },
    required: ["type", "block_id"],
  },
  {
    type: "object",
    additionalProperties: false,
    properties: {
      type: { type: "string", enum: ["patch_field"] },
      block_id: {
        type: "string",
        description:
          "The block's id (same string as the block_id key). Not a field id — if you use a Text field's id here, the tool will error with a hint.",
      },
      field_id: {
        type: "string",
        description:
          "Exact ShareDB field id: copy the VALUE of one structure key from that block — instructions_field_id, content_field_id, choice_field_id, or code_field_id (short opaque id). Not the block id.",
      },
      text_markdown: {
        type: "string",
        description:
          "For a Text field only: replace rich text from this markdown (use \\n for line breaks).",
      },
      choice_labels: {
        type: "array",
        items: { type: "string" },
        description:
          "For a ChoiceField only: new display labels in order; length must match the number of choices.",
        minItems: 1,
      },
      code_source: {
        type: "string",
        description:
          "For a CodeField only: replace the cell's starter source as plain text (not markdown; line breaks allowed).",
      },
    },
    required: ["type", "block_id", "field_id"],
  },
];

/**
 * JSON schema for modify_page_content: ordered operations (reordered server-side to deletes, then patches, then appends).
 */
export const worksheetSchema: AISchemaType = {
  $schema: "https://json-schema.org/draft/2020-12/schema",
  type: "object",
  description:
    "Worksheet edits for the current Page. Operations are applied in a fixed order: all delete_block, " +
    "then all patch_field, then all append — regardless of array order.",
  additionalProperties: false,
  properties: {
    operations: {
      type: "array",
      description: "List of worksheet operations to apply after the user accepts the preview.",
      minItems: 1,
      maxItems: 15,
      items: {
        oneOf: [...operationSchemaOneOf],
      },
    },
  },
  required: ["operations"],
};
